import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import api from '../api';
import { useAuth } from '../context/auth';
import { usePlayback } from '../context/playback';
import { PageTitle, HeavyText, LightItalicText } from '../components/Style';
import { Card } from '../components/FormStyle';
import StreamingServicesPicker from '../components/StreamingServicesPicker';

var Subtitle = styled.p`
  font-size: 0.8rem;
  color: #555;
  line-height: 1.5;
  margin: 0 0 1rem;
`;

var BackLink = styled(Link)`
  display: block;
  text-align: center;
  font-size: 0.8rem;
  color: #999;
  margin-top: 1rem;
  text-decoration: none;

  &:hover {
    color: #333;
  }
`;

var SaveStatus = styled.p`
  text-align: center;
  font-size: 0.8rem;
  font-weight: 600;
  margin: 0.75rem 0 0;
  min-height: 1.2rem;
  color: ${function (props) {
    if (props.$state === 'error') return 'tomato';
    if (props.$state === 'saved') return '#22c55e';
    return '#999';
  }};
`;

export default function StreamingPreferences() {
  useEffect(function () {
    document.title = 'Streaming Preferences | HeavyRotation';
  }, []);

  var authContext = useAuth();
  var authTokens = authContext.authTokens;
  var setAuthTokens = authContext.setAuthTokens;
  var navigate = useNavigate();
  var playback = usePlayback();

  var _selectedServices = useState([]);
  var selectedServices = _selectedServices[0];
  var setSelectedServices = _selectedServices[1];

  var _otherService = useState('');
  var otherService = _otherService[0];
  var setOtherService = _otherService[1];

  var _saving = useState(false);
  var saving = _saving[0];
  var setSaving = _saving[1];

  var _saved = useState(false);
  var saved = _saved[0];
  var setSaved = _saved[1];

  var _saveError = useState(false);
  var saveError = _saveError[0];
  var setSaveError = _saveError[1];

  // Serialized form of the value currently persisted on the server. Used to
  // skip no-op saves, and (via hydratedRef) to gate auto-save until the saved
  // value has loaded so an empty initial render never wipes the user's prefs.
  var savedValueRef = useRef(null);
  var hydratedRef = useRef(false);
  var optimisticPaintedRef = useRef(false);

  // Build the save payload: known services plus an optional "other:" entry.
  function buildServices(selected, other) {
    var services = selected.slice();
    if (other.trim()) {
      services.push('other:' + other.trim());
    }
    return services;
  }

  // Parse a comma-separated streamingServices string into the picker's
  // known-services array plus a free-text "other" value.
  function applyStreamingServices(value, authoritative) {
    var known = [];
    var other = '';
    if (value) {
      value.split(',').forEach(function (p) {
        if (p.startsWith('other:')) {
          other = p.slice(6);
        } else if (p) {
          known.push(p);
        }
      });
    }
    setSelectedServices(known);
    setOtherService(other);
    if (authoritative) {
      savedValueRef.current = buildServices(known, other).join(',');
      hydratedRef.current = true;
    }
  }

  // Optimistic paint (once) from the auth context for an instant fill. This
  // can be stale or missing (e.g. a fresh login on a new device), so the
  // server fetch below is the authoritative source.
  useEffect(
    function () {
      if (optimisticPaintedRef.current || hydratedRef.current) return;
      if (authTokens?.user?.streamingServices) {
        optimisticPaintedRef.current = true;
        applyStreamingServices(authTokens.user.streamingServices);
      }
    },
    [authTokens?.user?.streamingServices]
  );

  // Authoritative load: always pull the saved preferences from the server so
  // the page populates correctly even when the auth context lacks them, and
  // enable auto-save once we know the persisted value.
  useEffect(function () {
    api
      .get('/api/users/editprofile')
      .then(function (result) {
        applyStreamingServices(result.data.streamingServices, true);
      })
      .catch(function () {});
  }, []);

  // Auto-save any change (debounced) once the saved value has loaded.
  useEffect(
    function () {
      if (!hydratedRef.current) return;
      var services = buildServices(selectedServices, otherService);
      var serialized = services.join(',');
      if (serialized === savedValueRef.current) return;

      setSaved(false);
      setSaveError(false);
      setSaving(true);
      var timer = setTimeout(function () {
        api
          .post('/api/users/streaming-services', { services: services })
          .then(function () {
            savedValueRef.current = serialized;
            // Keep the auth context in sync so the rest of the app sees the
            // latest value without a refetch.
            if (authTokens) {
              setAuthTokens({
                token: authTokens.token,
                user: Object.assign({}, authTokens.user, {
                  streamingServices: services.length > 0 ? serialized : null,
                }),
              });
            }
            setSaving(false);
            setSaved(true);
          })
          .catch(function () {
            setSaving(false);
            setSaveError(true);
          });
      }, 600);

      return function () {
        clearTimeout(timer);
      };
    },
    [selectedServices, otherService]
  );

  return (
    <Card style={{ marginTop: '2rem' }}>
      <PageTitle>Streaming Preferences</PageTitle>

      <Subtitle>
        Select the streaming services you use.
        <br />
        <br />
        When you tap ••• on a post, you'll see options to search or open the music directly in your
        selected services.
      </Subtitle>

      <table
        style={{
          width: '100%',
          fontSize: '0.7rem',
          borderCollapse: 'collapse',
          color: '#555',
          marginBottom: '0.75rem',
        }}
      >
        <thead>
          <tr style={{ borderBottom: '1px solid #ddd', textAlign: 'left' }}>
            <th style={{ padding: '0.3rem 0', fontWeight: 700 }}>Service</th>
            <th style={{ padding: '0.3rem 0', fontWeight: 700 }}>Songs</th>
            <th style={{ padding: '0.3rem 0', fontWeight: 700 }}>Albums</th>
            <th style={{ padding: '0.3rem 0', fontWeight: 700 }}>In-App Play</th>
          </tr>
        </thead>
        <tbody>
          <tr style={{ borderBottom: '1px solid #eee' }}>
            <td style={{ padding: '0.3rem 0', fontWeight: 600 }}>Apple Music</td>
            <td style={{ padding: '0.3rem 0' }}>Direct link</td>
            <td style={{ padding: '0.3rem 0' }}>Direct link</td>
            <td style={{ padding: '0.3rem 0' }}>Full songs + previews</td>
          </tr>
          <tr style={{ borderBottom: '1px solid #eee' }}>
            <td style={{ padding: '0.3rem 0', fontWeight: 600 }}>Tidal</td>
            <td style={{ padding: '0.3rem 0' }}>Direct link</td>
            <td style={{ padding: '0.3rem 0' }}>Direct link</td>
            <td style={{ padding: '0.3rem 0' }}>--</td>
          </tr>
          <tr style={{ borderBottom: '1px solid #eee' }}>
            <td style={{ padding: '0.3rem 0', fontWeight: 600 }}>Deezer</td>
            <td style={{ padding: '0.3rem 0' }}>Direct link</td>
            <td style={{ padding: '0.3rem 0' }}>Direct link</td>
            <td style={{ padding: '0.3rem 0' }}>--</td>
          </tr>
          <tr style={{ borderBottom: '1px solid #eee' }}>
            <td style={{ padding: '0.3rem 0', fontWeight: 600 }}>Spotify</td>
            <td style={{ padding: '0.3rem 0' }}>Search</td>
            <td style={{ padding: '0.3rem 0' }}>Search</td>
            <td style={{ padding: '0.3rem 0' }}>--</td>
          </tr>
          <tr>
            <td style={{ padding: '0.3rem 0', fontWeight: 600 }}>Others</td>
            <td style={{ padding: '0.3rem 0' }}>Search</td>
            <td style={{ padding: '0.3rem 0' }}>Search</td>
            <td style={{ padding: '0.3rem 0' }}>--</td>
          </tr>
        </tbody>
      </table>

      <div
        style={{
          fontSize: '0.65rem',
          color: '#999',
          fontStyle: 'italic',
          textAlign: 'center',
          marginBottom: '1rem',
        }}
      >
        While we'd love to support all streaming services, not all of them make their data available
        for public consumption!
      </div>

      <Subtitle style={{ margin: '0 0 0.5rem' }}>Select all that apply.</Subtitle>

      <StreamingServicesPicker
        selected={selectedServices}
        onChange={setSelectedServices}
        otherValue={otherService}
        onOtherChange={setOtherService}
      />

      <Subtitle style={{ marginTop: '0.75rem' }}>
        If you have an Apple Music subscription, connect it to stream full songs directly on{' '}
        <HeavyText>Heavy</HeavyText>
        <LightItalicText>Rotation</LightItalicText>. Otherwise, previews will play automatically.
      </Subtitle>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          margin: '0.5rem 0 1rem',
        }}
      >
        {playback.musicKitLoading ? (
          <span style={{ fontSize: '0.8rem', color: '#999' }}>Loading Apple Music...</span>
        ) : playback.isAppleMusicConnected ? (
          <>
            <span style={{ fontSize: '0.8rem', color: '#4caf50' }}>Apple Music connected</span>
            <button
              type="button"
              onClick={function () {
                playback.unauthorize();
              }}
              style={{
                background: 'white',
                border: '1px solid #ccc',
                borderRadius: '20px',
                padding: '0.25rem 0.75rem',
                fontSize: '0.7rem',
                cursor: 'pointer',
              }}
            >
              Disconnect
            </button>
          </>
        ) : (
          <button
            type="button"
            onClick={function () {
              playback.authorize();
            }}
            style={{
              background: '#fa243c',
              color: 'white',
              border: 'none',
              borderRadius: '20px',
              padding: '0.35rem 1rem',
              fontSize: '0.75rem',
              cursor: 'pointer',
            }}
          >
            Connect Apple Music
          </button>
        )}
      </div>

      <SaveStatus $state={saveError ? 'error' : saved ? 'saved' : 'idle'} aria-live="polite">
        {saving
          ? 'Saving...'
          : saveError
            ? 'Could not save. Check your connection.'
            : saved
              ? 'Saved'
              : 'Changes save automatically'}
      </SaveStatus>

      <BackLink to="/profile/edit">Back to Edit Profile</BackLink>
    </Card>
  );
}
